import { createContext, useContext } from "react";
import {
  getNextSetupStep,
  getPreviousSetupStep,
  getSetupStepIndex,
  isSetupStepId,
  type SetupSteps,
} from "./setupSteps";

/** The steps for an online course, which starts with its SCORM file. */
export const COURSE_SETUP_STEPS = [
  { id: "file", title: "Course file" },
  { id: "about", title: "About the course" },
  { id: "audience", title: "Who it's for" },
  { id: "picture", title: "Course picture" },
  { id: "modules", title: "Modules" },
  { id: "review", title: "Check and publish" },
] as const;

/**
 * The steps for an instructor-led course. It has no course file or modules, so those steps are
 * skipped; it gets a price step instead.
 */
export const INSTRUCTOR_LED_SETUP_STEPS = [
  { id: "about", title: "About the course" },
  { id: "audience", title: "Who it's for" },
  { id: "picture", title: "Course picture" },
  { id: "price", title: "Price" },
  { id: "review", title: "Check and publish" },
] as const;

export type CourseSetupStepId =
  (typeof COURSE_SETUP_STEPS)[number]["id"] | (typeof INSTRUCTOR_LED_SETUP_STEPS)[number]["id"];

export type CourseSetupStepList = SetupSteps<CourseSetupStepId>;

/** The step a draft opens on when the URL has a course but no step. */
export const DEFAULT_COURSE_SETUP_STEP: CourseSetupStepId = "about";

export const getCourseSetupSteps = (isInstructorLed: boolean): CourseSetupStepList =>
  isInstructorLed ? INSTRUCTOR_LED_SETUP_STEPS : COURSE_SETUP_STEPS;

/** The step list of the course being set up. Online unless the flow says otherwise. */
export const CourseSetupStepsContext = createContext<CourseSetupStepList>(COURSE_SETUP_STEPS);

export const useCourseSetupSteps = () => useContext(CourseSetupStepsContext);

const ALL_STEP_IDS: CourseSetupStepList = [...COURSE_SETUP_STEPS, ...INSTRUCTOR_LED_SETUP_STEPS];

export const isCourseSetupStepId = (value: unknown): value is CourseSetupStepId =>
  isSetupStepId(ALL_STEP_IDS, value);

export const getCourseSetupStepIndex = (
  stepId: CourseSetupStepId,
  steps: CourseSetupStepList = COURSE_SETUP_STEPS,
): number => getSetupStepIndex(steps, stepId);

export const getCourseSetupStepTitle = (
  stepId: CourseSetupStepId,
  steps: CourseSetupStepList = COURSE_SETUP_STEPS,
): string => steps[getCourseSetupStepIndex(stepId, steps)]?.title ?? "";

export const getNextCourseSetupStep = (
  stepId: CourseSetupStepId,
  steps: CourseSetupStepList = COURSE_SETUP_STEPS,
): CourseSetupStepId | null => getNextSetupStep(steps, stepId);

export const getPreviousCourseSetupStep = (
  stepId: CourseSetupStepId,
  steps: CourseSetupStepList = COURSE_SETUP_STEPS,
): CourseSetupStepId | null => getPreviousSetupStep(steps, stepId);
